import type { XlsxSelectionContext, XlsxViewer } from "@silurus/ooxml/xlsx";
import type { PptxViewer, ZoomableViewer } from "@silurus/ooxml/pptx";
import type { OfficePreviewFormat } from "./officePreview";

export type OfficeViewer = Pick<XlsxViewer, "load" | "destroy"> &
  Pick<ZoomableViewer, "zoomIn" | "zoomOut" | "fitWidth" | "fitPage"> &
  Partial<Pick<PptxViewer, "nextSlide" | "prevSlide">>;

async function loadViewerConstructor(format: OfficePreviewFormat) {
  switch (format) {
    case "docx":
      return {
        format,
        Viewer: (await import("@silurus/ooxml/docx")).DocxScrollViewer,
      };
    case "xlsx":
      return {
        format,
        Viewer: (await import("@silurus/ooxml/xlsx")).XlsxViewer,
      };
    case "pptx": {
      const { PptxViewer, autoResize } = await import("@silurus/ooxml/pptx");
      return { format, Viewer: PptxViewer, autoResize };
    }
  }
}

export async function loadOfficeViewer(format: OfficePreviewFormat) {
  const [renderer, { math }, { chartEx }, { threeD }, { regionMap }, { tiff }] =
    await Promise.all([
      loadViewerConstructor(format),
      import("@silurus/ooxml/math"),
      import("@silurus/ooxml/chart-ex"),
      import("@silurus/ooxml/three-d"),
      import("@silurus/ooxml/region-map"),
      import("@silurus/ooxml/tiff"),
    ]);
  return (
    container: HTMLElement,
    onError: (error: Error) => void,
    onSpreadsheetSelection: (context: XlsxSelectionContext | null) => void,
    onSlideChange: (index: number, total: number) => void
  ): OfficeViewer => {
    const options = {
      math,
      chartEx,
      threeD,
      regionMap,
      tiff,
      useGoogleFonts: false,
      enableHyperlinks: false,
      enableMediaPlayback: false,
      enableTextSelection: true,
      hiddenSheetMode: "skip" as const,
      showZoomSlider: false,
      workerTimeoutMs: 30_000,
      onError,
    };
    if (renderer.format === "pptx") {
      const canvas = container.ownerDocument.createElement("canvas");
      Object.assign(container.style, {
        display: "grid",
        placeItems: "safe center",
        overflow: "auto",
      });
      container.append(canvas);
      const viewer = new renderer.Viewer(canvas, { ...options, onSlideChange });
      let stopResize: (() => void) | undefined;
      let destroyed = false;
      return {
        async load(bytes) {
          await viewer.load(bytes);
          if (destroyed) return;
          await viewer.fitPage();
          if (destroyed) return;
          stopResize = renderer.autoResize(
            () => viewer.fitPage().catch(onError),
            container
          );
        },
        destroy() {
          destroyed = true;
          stopResize?.();
          viewer.destroy();
        },
        zoomIn: () => viewer.zoomIn(),
        zoomOut: () => viewer.zoomOut(),
        fitWidth: () => viewer.fitWidth(),
        fitPage: () => viewer.fitPage(),
        nextSlide: () => viewer.nextSlide(),
        prevSlide: () => viewer.prevSlide(),
      };
    }
    return renderer.format === "xlsx"
      ? new renderer.Viewer(container, {
          ...options,
          onSelectionContextChange: onSpreadsheetSelection,
        })
      : new renderer.Viewer(container, options);
  };
}
